/**
 * Copyright Mántum All Rights Reserved.
 */
import Input from '../input';
import { validator } from '@mantum/kanela';

import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import type { Validation, Detail, Constraint, Limit } from '@mantum/kanela';

export type PropTypes = {
    advanced?: {
        alert?: 'success' | 'error' | null;
        frozen?: boolean;
        hidden?: boolean;
        limit?: Limit;
        max?: number;
        min?: number;
        mode?: 'display' | 'normal';
        mutate?: boolean;
        report?: boolean;
    };
    disabled?: boolean;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    optional?: boolean;
    placeholder?: string;
    resize?: boolean;
    testid?: string;
    validation?: {
        '<'?: Constraint & { value: number };
        '<='?: Constraint & { value: number };
        '>'?: Constraint & { value: number };
        '>='?: Constraint & { value: number };
    } & Validation;
    value?: number;
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function NumberBox(props, ref) {
        // prettier-ignore
        const {
            optional = true
        } = props;

        const [error, setError] = useState(false);
        const [message, setMessage] = useState(props.validation?.message);

        const validationRef = useRef<Detail<string>>(null);

        const validate = useCallback(() => {
            setError(false);
            if (!optional && !validationRef.current?.validate()) {
                setError(true);
                setMessage(props.validation?.message);
                return false;
            }

            const current = validationRef.current?.value();
            if (current) {
                const value = window.Number(current);

                if (props.validation?.['<=']?.enabled) {
                    const error = validator.isLessThanThanOrEqualTo(
                        value,
                        props.validation['<='].value
                    );

                    if (error) {
                        setError(true);
                        setMessage(props.validation['<='].message);
                        return false;
                    }
                }

                if (props.validation?.['<']?.enabled) {
                    const error = validator.isLessThan(
                        value,
                        props.validation['<'].value
                    );

                    if (error) {
                        setError(true);
                        setMessage(props.validation['<'].message);
                        return false;
                    }
                }

                if (props.validation?.['>=']?.enabled) {
                    const error = validator.isGreaterThanOrEqualTo(
                        value,
                        props.validation['>='].value
                    );

                    if (error) {
                        setError(true);
                        setMessage(props.validation['>='].message);
                        return false;
                    }
                }

                if (props.validation?.['>']?.enabled) {
                    const error = validator.isGreaterThan(
                        value,
                        props.validation['>'].value
                    );

                    if (error) {
                        setError(true);
                        setMessage(props.validation['>'].message);
                        return false;
                    }
                }
            }

            return true;
        }, [optional, props.validation]);

        useImperativeHandle(
            ref,
            () => {
                return {
                    name: validationRef.current?.name,
                    validable: true,
                    clear() {
                        setError(false);
                        validationRef.current?.clear();
                    },
                    validate() {
                        if (!validate()) {
                            return false;
                        }
                        return true;
                    },
                    value() {
                        return validationRef.current?.value();
                    }
                };
            },
            [validate]
        );

        const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            validate();
            if (props.onBlur) {
                props.onBlur(event);
            }
        };

        return (
            <Input
                alert={props.advanced?.alert}
                autocomplete='off'
                resize={props.resize}
                disabled={props.disabled}
                mode={props.advanced?.mode}
                error={error}
                frozen={props.advanced?.frozen}
                hidden={props.advanced?.hidden}
                limit={props.advanced?.limit}
                max={props.advanced?.max}
                message={message}
                min={props.advanced?.min}
                name={props.name}
                omitValidation={true}
                onBlur={onBlur}
                onChange={props.onChange}
                onKeyDown={props.onKeyDown}
                optional={optional}
                placeholder={props.placeholder}
                ref={validationRef}
                report={props.advanced?.report}
                testid={props.testid}
                mutate={props.advanced?.mutate}
                type='number'
                value={props.value}
            />
        );
    }
);
